// JOBE-0163.3 — OkrsFraming: OKRs framing for W4 DepartmentPage.
// Default framing (?framing=okrs). Objectives as Card headers; Key Results
// as an inset table with progress bar, Spark (absolute mode), and status pills.

import { Card }       from '../components/Card/Card.jsx';
import { Spark }      from '../components/Spark/Spark.jsx';
import { StatusPill } from '../components/StatusPill/StatusPill.jsx';
import './OkrsFraming.css';

// Map KR tone to StatusPill { tone, label }.
// Spark TONES only supports ok|warn|bad|ink; neutral maps to ink for Spark.
const KR_PILL = {
  ok:      { tone: 'ok',     label: 'On track' },
  warn:    { tone: 'warn',   label: 'Watch' },
  bad:     { tone: 'bad',    label: 'Behind' },
  neutral: { tone: 'pencil', label: 'Pending' },
};

function krPill(tone) {
  return KR_PILL[tone] ?? { tone: 'pencil', label: tone };
}

// Map KR fixture tone to a Spark-valid tone. Spark accepts ok|warn|bad|ink.
function sparkTone(tone) {
  if (['ok', 'warn', 'bad'].includes(tone)) return tone;
  return 'ink';
}

// Progress toward target — expressed as 0..1.
// Heuristic: if tone is 'bad' and current > target → lower-is-better.
//            if tone is 'bad' and current < target → higher-is-better.
//            otherwise treat as higher-is-better (default).
function krProgress(kr) {
  if (!kr.target) return null;
  const lowerIsBetter = kr.tone === 'bad' && kr.current > kr.target;
  const higherIsBetter = kr.tone === 'bad' && kr.current < kr.target;
  if (lowerIsBetter) return Math.min(kr.target / kr.current, 1);
  if (higherIsBetter) return Math.min(kr.current / kr.target, 1);
  return kr.current >= kr.target ? 1 : kr.current / kr.target;
}

export default function OkrsFraming({ dept }) {
  const okrs = dept?.okrs ?? [];

  if (okrs.length === 0) {
    return <p className="okrs-empty">No OKRs defined for this department.</p>;
  }

  return (
    <div className="okrs-framing" data-testid="framing-okrs">
      {okrs.map((obj, i) => (
        <Card key={i} className="objective-card">
          <h2 className="objective-title">{obj.objective}</h2>
          <table className="kr-table">
            <thead>
              <tr>
                <th>Key Result</th>
                <th>Current</th>
                <th>Target</th>
                <th>Progress</th>
                <th>Trend</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {obj.keyResults.map((kr, j) => {
                const progress = krProgress(kr);
                const { tone: pillTone, label: pillLabel } = krPill(kr.tone);
                const hasTarget = kr.target != null;
                const hasSpark  = Array.isArray(kr.spark) && kr.spark.length > 0;
                return (
                  <tr key={j} className="kr-row">
                    <td className="kr-title" title={kr.title}>
                      {kr.title.length > 60 ? kr.title.slice(0, 60) + '…' : kr.title}
                    </td>
                    <td className="kr-current mono">
                      {kr.current}{kr.unit ? ' ' + kr.unit : ''}
                    </td>
                    <td className="kr-target mono">
                      {hasTarget ? `${kr.target}${kr.unit ? ' ' + kr.unit : ''}` : '—'}
                    </td>
                    <td className="kr-progress">
                      {progress != null
                        ? <progress value={progress} max={1} data-tone={kr.tone} />
                        : '—'}
                    </td>
                    <td className="kr-spark">
                      {hasSpark && hasTarget
                        ? <Spark
                            mode="absolute"
                            data={kr.spark}
                            target={kr.target}
                            tone={sparkTone(kr.tone)}
                            unit={kr.unit || ''}
                            ariaLabel={kr.title}
                          />
                        : '—'}
                    </td>
                    <td className="kr-status">
                      <StatusPill tone={pillTone} label={pillLabel} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ))}
    </div>
  );
}
